import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, ChevronDown, Clock, Sparkles, IndianRupee, XCircle } from "lucide-react";
import { applyPricingLevel } from "@lawyer/services/lawyerApi";

const fmt = (p) => `₹${Math.round(p / 100).toLocaleString("en-IN")}`;
const fmtFee = (p) => {
  const r = (p || 0) / 100;
  return `₹${r.toLocaleString("en-IN", Number.isInteger(r) ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const MODES = [["chat", "Chat", 5000], ["audio", "Audio", 7500], ["video", "Video", 10000]];
const cardCls = "rounded-2xl border border-slate-200 bg-white p-5";
const eyebrow = "text-[11px] uppercase tracking-widest font-bold text-slate-400";

function CurrentLevel({ active, modes }) {
  const fee = active.active_base_fee_paise;
  return (
    <div className="rounded-2xl bg-[#0A2540] text-white p-5" data-testid="pricing-active-banner">
      <p className={`${eyebrow} text-white/60`}>Current level</p>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0">
        <p className="text-2xl sm:text-3xl font-extrabold" style={{ fontFamily: "Poppins" }} data-testid="pricing-current-level">Level {active.active_level}</p>
        <p className="text-2xl sm:text-3xl font-extrabold text-[#D4AF37]" style={{ fontFamily: "Poppins" }} data-testid="pricing-live-fee">{fmt(fee)}</p>
      </div>
      <p className="text-xs text-white/70">Video · 15 min base fee</p>
      <div className="mt-4 grid grid-cols-3 gap-2" data-testid="pricing-mode-fees">
        {MODES.map(([k, label, dflt]) => (
          <div key={k} className="rounded-xl bg-white/10 px-3 py-2 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-white/60 font-bold">{label}</p>
            <p className="font-bold text-sm truncate" data-testid={`pricing-mode-fee-${k}`}>{fmtFee(fee * (modes[k] || dflt) / 10000)}</p>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-white/60" data-testid="pricing-active-meta">
        {active.approved_at ? `Approved ${new Date(active.approved_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · ` : ""}Level changes require Admin approval.
      </p>
    </div>
  );
}

function StatusBanner({ data }) {
  const pending = data.pending_application;
  if (pending) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="pricing-pending-banner">
        <Clock size={18} className="text-amber-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-bold text-amber-900">
            {pending.status === "custom_review" ? "Custom pricing under admin review" : "Level change under review"}
          </p>
          <p className="text-xs text-amber-800/80 mt-0.5">
            Requested Level {pending.requested_level}{pending.custom_fee_paise ? ` · proposed fee ${fmt(pending.custom_fee_paise)}` : ""}.
          </p>
        </div>
      </div>
    );
  }
  if (data.active?.status === "approved") return null;
  if (data.last_decision?.status === "rejected") {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-3" data-testid="pricing-rejected-banner">
        <XCircle size={18} className="text-rose-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-bold text-rose-900">Your last pricing application was not approved</p>
          <p className="text-xs text-rose-800/80 mt-0.5">
            {data.last_decision.admin_reason || "Please review and apply again."}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className={cardCls} data-testid="pricing-not-applied-banner">
      <p className="text-sm font-bold text-[#0A2540]">No pricing level yet</p>
      <p className="text-xs text-slate-500 mt-0.5">
        Apply for a pricing level below. Until approved, your profile uses default pricing.
      </p>
    </div>
  );
}

function LevelOption({ label, price, selected, recommended, current, dashed, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={selected}
            className={`rounded-xl border px-3 py-2.5 text-left transition-all flex items-center justify-between gap-2 min-w-0 ${
              selected ? "border-[#0A2540] bg-[#0A2540] text-white shadow-lg" : `${dashed ? "border-dashed border-slate-300" : current ? "border-[#D4AF37] bg-[#FBF4DF]/60" : "border-slate-200"} bg-white hover:border-[#0A2540]`}`}>
      <span className="min-w-0">
        <span className={`block text-[10px] font-bold uppercase tracking-wider truncate ${selected ? "text-[#D4AF37]" : "text-slate-400"}`}>{label}{recommended ? " ★" : ""}{current ? " · Current" : ""}</span>
        <span className="block font-extrabold text-sm truncate">{price}</span>
      </span>
      {selected && <Check size={16} className="shrink-0 text-[#D4AF37]" aria-hidden="true" />}
    </button>
  );
}

/**
 * LevelRequestForm — reusable level-assignment request UI.
 *
 * Reuses the existing `/lawyer/pricing/apply` endpoint and the same eligibility,
 * validation, and status handling as the Lawyer Pricing page. Render it wherever
 * a lawyer should be able to request or change their pricing level.
 */
export default function LevelRequestForm({ data, setData, onSubmitted, className }) {
  const [selected, setSelected] = useState(null);
  const [customFee, setCustomFee] = useState("");
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!data) return;
    if (!data.pending_application) {
      setSelected(data.active?.status === "approved" ? data.active.active_level : data.recommended_level);
    }
  }, [data]);

  if (!data) return <div className="text-slate-500" data-testid="level-request-form-loading">Loading…</div>;

  const modes = data.mode_multipliers_bp || {};
  const canApply = !data.pending_application;
  const hasActive = data.active?.status === "approved";
  const recommended = data.levels.find((l) => l.level === data.recommended_level);

  const submit = async () => {
    if (!selected) return toast.error("Please select a pricing level");
    if (selected === data.custom_level && (!customFee || Number(customFee) < 1)) {
      return toast.error("Please enter your proposed custom fee");
    }
    setBusy(true);
    try {
      const app = await applyPricingLevel({
        requested_level: selected,
        ...(selected === data.custom_level ? { custom_fee_rupees: Number(customFee) } : {}),
      });
      toast.success("Application submitted for admin review");
      setData((d) => ({ ...d, pending_application: app }));
      setPickerOpen(false);
      if (onSubmitted) onSubmitted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit application"); }
    setBusy(false);
  };

  const picker = (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {data.levels.map((l) => (
          <LevelOption key={l.level} label={`Level ${l.level}`} price={`₹${l.base_fee_rupees.toLocaleString("en-IN")}`} selected={selected === l.level}
                       recommended={!hasActive && l.level === data.recommended_level} current={hasActive && data.active.active_level === l.level}
                       onClick={() => setSelected(l.level)} testid={`pricing-level-${l.level}`} />
        ))}
        <LevelOption label="Level 11" price="Custom" dashed selected={selected === data.custom_level} onClick={() => setSelected(data.custom_level)} testid="pricing-level-11" />
      </div>
      {selected === data.custom_level && (
        <div className="mt-4">
          <label className="text-xs font-bold text-slate-500">Proposed video 15-min fee (₹)</label>
          <div className="relative mt-1 max-w-[220px]">
            <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input type="number" min={1} value={customFee} onChange={(e) => setCustomFee(e.target.value)}
              className="pl-8" data-testid="pricing-custom-fee-input" placeholder="2500" />
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Custom pricing always requires a manual admin review.</p>
        </div>
      )}
      <div className="mt-5 flex justify-end">
        <Button onClick={submit} disabled={busy} data-testid="pricing-apply-btn" className="rounded-xl adv-btn-gold font-bold w-full sm:w-auto">
          {busy ? "Submitting…" : "Submit for admin approval"}
        </Button>
      </div>
    </>
  );

  return (
    <div className={`space-y-4 ${className || ""}`} data-testid="level-request-form">
      <StatusBanner data={data} />
      {data.active?.status === "approved" && <CurrentLevel active={data.active} modes={modes} />}

      {!hasActive && (
        <div className="rounded-2xl border border-[#F1E3B5] bg-[#FBF4DF] px-4 py-3 flex items-start gap-3" data-testid="pricing-recommendation">
          <Sparkles size={16} className="text-[#B8962E] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#0A2540]" data-testid="pricing-recommendation-level">
              Suggested level: Level {data.recommended_level}{recommended ? ` · ₹${recommended.base_fee_rupees.toLocaleString("en-IN")}` : ""}
            </p>
            <p className="text-xs text-[#0A2540]/70">Based on your {data.yoe} years of experience. All level changes require Admin approval.</p>
          </div>
        </div>
      )}

      {canApply && !hasActive && (
        <div className={cardCls} data-testid="pricing-level-picker">
          <p className={`${eyebrow} mb-3`}>Select a level to apply</p>
          {picker}
        </div>
      )}

      {canApply && hasActive && (
        <>
          <div className="flex justify-end">
            <Button onClick={() => setPickerOpen(true)} variant="outline" data-testid="pricing-request-change-btn" className="rounded-xl font-bold border-[#0A2540] text-[#0A2540] w-full sm:w-auto">
              Request level change
            </Button>
          </div>
          <Dialog open={pickerOpen} onOpenChange={(o) => { if (!busy) setPickerOpen(o); }}>
            <DialogContent className="w-[calc(100vw-1rem)] max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl p-5 sm:p-6" data-testid="pricing-level-picker">
              <DialogHeader className="text-left">
                <DialogTitle className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Request a level change</DialogTitle>
                <DialogDescription className="text-xs" data-testid="pricing-fresh-assessment-note">
                  A fresh level assessment will be done by the Advoset Admin team before any change is applied. Your current Level {data.active.active_level} stays active until then.
                </DialogDescription>
              </DialogHeader>
              {picker}
            </DialogContent>
          </Dialog>
        </>
      )}

      <details className={`${cardCls} group`} data-testid="pricing-how-it-works">
        <summary className="flex items-center justify-between cursor-pointer list-none [&::-webkit-details-marker]:hidden" data-testid="pricing-how-it-works-toggle">
          <span className="text-sm font-bold text-[#0A2540]">How pricing works</span>
          <ChevronDown size={16} className="text-slate-400 transition-transform group-open:rotate-180" />
        </summary>
        <ul className="mt-3 text-sm text-slate-600 space-y-1.5" data-testid="pricing-how-it-works-body">
          <li>Your level sets the <span className="font-bold">Video · 15 min</span> base fee.</li>
          <li>Chat = {(modes.chat || 5000) / 10000}× · Audio = {(modes.audio || 7500) / 10000}× · Video = {(modes.video || 10000) / 10000}×</li>
          <li>15 min = 1× · 30 min = 2×</li>
          <li>Final client price is rounded up to the nearest rupee.</li>
        </ul>
      </details>
    </div>
  );
}
